import * as amplitude from "@amplitude/analytics-browser";
import { lazyComponent } from "@redotech/react-util/component";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import EditSquareIcon from "@redotech/redo-icons/edit-05.svg";
import CloseIcon from "@redotech/redo-icons/x-close.svg";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { RedoButton } from "../../../arbiter-components/buttons/redo-button";
import { RedoIcon } from "../../../arbiter-components/icon/redo-icon";
import { Dropdown } from "../../../dropdown";
import { Flex } from "../../../flex";
import { Text } from "../../../text";
import { AiChatContext } from "../contexts/ai-chat-context";

const ChatWithThread = lazyComponent(async () => {
  const { ChatWithThread } = await import("./chat-with-thread");
  return ChatWithThread;
});

// Advanced chat component with thread history, dynamic titles, and enhanced features
// This component is used when the globalAI feature flag is enabled
export const AiSlideOutChatGlobal = memo(function AiSlideOutChatGlobal({
  onClose,
  belowChatButtons,
  size = "sm",
  threads = [],
  currentThreadId,
  onLoadThread,
  onNewConversation,
}: {
  onClose: () => void;
  belowChatButtons?: { text: string; onClick: () => void }[];
  size?: "sm" | "lg";
  threads?: any[];
  currentThreadId?: string;
  onLoadThread?: (threadId: string, thread: any) => void;
  onNewConversation?: () => void;
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const chevronRef = useRef<HTMLDivElement>(null);
  const { messages } = useContext(AiChatContext);

  // Dynamic title logic
  const getDynamicTitle = useCallback(() => {
    // Check if there are any user messages
    const userMessages = messages.filter((msg) => msg.role === "user");

    if (userMessages.length > 0) {
      // Use the first user message as the conversation name
      const firstMessage = userMessages[0].content;
      if (typeof firstMessage === "string" && firstMessage.trim()) {
        // Truncate if too long
        return firstMessage.length > 30
          ? firstMessage.substring(0, 30) + "..."
          : firstMessage;
      }
    }

    // Default to "New conversation"
    return "New conversation";
  }, [messages]);

  const dynamicTitle = getDynamicTitle();

  // Check if there are any threads to show
  const hasThreads = threads && threads.length > 0;

  const handleChevronClick = useCallback(() => {
    // Only handle click if there are threads to show
    if (!hasThreads) return;

    if (!dropdownOpen) {
      amplitude.logEvent("merchant-support/thread-history-dropdown-opened");
    }
    setDropdownOpen(!dropdownOpen);
  }, [dropdownOpen, hasThreads]);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownOpen &&
        chevronRef.current &&
        !chevronRef.current.contains(event.target as Node)
      ) {
        // Check if click is not inside the dropdown content
        const dropdownElement = document.getElementById("thread-dropdown");
        if (
          !dropdownElement ||
          !dropdownElement.contains(event.target as Node)
        ) {
          setDropdownOpen(false);
        }
      }
    };

    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  return (
    <div data-exclude-text-highlight="true" style={{ height: "100%" }}>
      <Flex bgColor="secondary" dir="column" height="full" overflow="hidden">
        <Flex
          align="center"
          flexDirection="row"
          justify="space-between"
          px="lg"
          py="md"
        >
          <Flex
            align="center"
            onClick={handleChevronClick}
            onMouseEnter={(e) => {
              if (hasThreads) {
                e.currentTarget.style.backgroundColor =
                  "var(--color-bg-secondary-subtle)";
              }
            }}
            onMouseLeave={(e) => {
              if (hasThreads) {
                e.currentTarget.style.backgroundColor = "transparent";
              }
            }}
            ref={chevronRef}
            style={{
              cursor: hasThreads ? "pointer" : "default",
              padding: "4px",
              borderRadius: "4px",
              transition: "background-color 0.2s",
            }}
          >
            <Text fontSize="md" fontWeight="medium">
              {dynamicTitle}
            </Text>
            {hasThreads && (
              <div style={{ marginLeft: "1px" }}>
                <RedoIcon color="secondary" Icon={ChevronDownIcon} size={16} />
              </div>
            )}
          </Flex>
          <Flex align="center" gap="xs">
            <RedoButton
              data-testid="new-conversation-button"
              hierarchy="tertiary"
              IconLeading={EditSquareIcon}
              onClick={onNewConversation}
              size="md"
            />
            <RedoButton
              data-testid="chat-close-button"
              hierarchy="tertiary"
              IconLeading={CloseIcon}
              onClick={onClose}
              size="md"
            />
          </Flex>
        </Flex>

        <ChatWithThread
          belowChatButtons={belowChatButtons}
          style="high-contrast"
        />
      </Flex>

      {/* Thread History Dropdown - Only render if there are threads */}
      {hasThreads && (
        <Dropdown
          anchor={chevronRef.current}
          fitToAnchor={false}
          flexProps={{
            id: "thread-dropdown",
            style: {
              width: "280px",
              maxHeight: "400px",
              overflowY: "auto",
              marginLeft: "-120px",
            },
          }}
          open={dropdownOpen}
          placement="bottom-start"
        >
          {(() => {
            // Group threads by time periods
            const now = new Date();
            const today = new Date(
              now.getFullYear(),
              now.getMonth(),
              now.getDate(),
            );
            const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
            const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
            const monthAgo = new Date(
              today.getTime() - 30 * 24 * 60 * 60 * 1000,
            );

            const groups = {
              today: [] as any[],
              yesterday: [] as any[],
              thisWeek: [] as any[],
              thisMonth: [] as any[],
            };

            for (const thread of threads) {
              const threadDate = new Date(thread.updatedAt);
              if (threadDate >= today) {
                groups.today.push(thread);
              } else if (threadDate >= yesterday) {
                groups.yesterday.push(thread);
              } else if (threadDate >= weekAgo) {
                groups.thisWeek.push(thread);
              } else if (threadDate >= monthAgo) {
                groups.thisMonth.push(thread);
              }
            }

            return (
              <>
                {groups.today.length > 0 && (
                  <>
                    <Text
                      color="primary"
                      fontSize="sm"
                      fontWeight="semibold"
                      style={{ marginBottom: "4px", marginTop: "0px" }}
                    >
                      Today
                    </Text>
                    {groups.today.map((thread) => {
                      const isCurrentThread = currentThreadId === thread._id;
                      const threadTitle =
                        thread.messages.find((msg: any) => msg.role === "user")
                          ?.content || `Conversation ${thread._id.slice(-6)}`;

                      return (
                        <div
                          key={thread._id}
                          onClick={() => {
                            onLoadThread?.(thread._id, thread);
                            setDropdownOpen(false);
                          }}
                          onMouseEnter={(e) => {
                            if (!isCurrentThread) {
                              e.currentTarget.style.backgroundColor =
                                "var(--color-bg-secondary-subtle)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isCurrentThread) {
                              e.currentTarget.style.backgroundColor =
                                "transparent";
                            }
                          }}
                          style={{
                            padding: "2px 4px",
                            marginBottom: "1px",
                            borderRadius: "2px",
                            cursor: "pointer",
                            transition: "background-color 0.2s",
                            backgroundColor: isCurrentThread
                              ? "var(--color-bg-brand-subtle)"
                              : "transparent",
                            border: isCurrentThread
                              ? "1px solid var(--color-border-brand)"
                              : "1px solid transparent",
                          }}
                        >
                          <Flex align="center" gap="xs">
                            <Text
                              fontSize="sm"
                              fontWeight="regular"
                              style={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: "180px",
                              }}
                            >
                              {threadTitle}
                            </Text>
                            {isCurrentThread && (
                              <Text
                                color="brand"
                                fontSize="xs"
                                fontWeight="medium"
                              >
                                (Current)
                              </Text>
                            )}
                          </Flex>
                        </div>
                      );
                    })}
                  </>
                )}

                {groups.yesterday.length > 0 && (
                  <>
                    <Text
                      color="primary"
                      fontSize="sm"
                      fontWeight="semibold"
                      style={{
                        marginBottom: "4px",
                        marginTop: groups.today.length > 0 ? "8px" : "0px",
                      }}
                    >
                      Yesterday
                    </Text>
                    {groups.yesterday.map((thread) => {
                      const isCurrentThread = currentThreadId === thread._id;
                      const threadTitle =
                        thread.messages.find((msg: any) => msg.role === "user")
                          ?.content || `Conversation ${thread._id.slice(-6)}`;

                      return (
                        <div
                          key={thread._id}
                          onClick={() => {
                            onLoadThread?.(thread._id, thread);
                            setDropdownOpen(false);
                          }}
                          onMouseEnter={(e) => {
                            if (!isCurrentThread) {
                              e.currentTarget.style.backgroundColor =
                                "var(--color-bg-secondary-subtle)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isCurrentThread) {
                              e.currentTarget.style.backgroundColor =
                                "transparent";
                            }
                          }}
                          style={{
                            padding: "2px 4px",
                            marginBottom: "1px",
                            borderRadius: "2px",
                            cursor: "pointer",
                            transition: "background-color 0.2s",
                            backgroundColor: isCurrentThread
                              ? "var(--color-bg-brand-subtle)"
                              : "transparent",
                            border: isCurrentThread
                              ? "1px solid var(--color-border-brand)"
                              : "1px solid transparent",
                          }}
                        >
                          <Flex align="center" gap="xs">
                            <Text
                              fontSize="sm"
                              fontWeight="regular"
                              style={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: "180px",
                              }}
                            >
                              {threadTitle}
                            </Text>
                            {isCurrentThread && (
                              <Text
                                color="brand"
                                fontSize="xs"
                                fontWeight="medium"
                              >
                                (Current)
                              </Text>
                            )}
                          </Flex>
                        </div>
                      );
                    })}
                  </>
                )}

                {groups.thisWeek.length > 0 && (
                  <>
                    <Text
                      color="primary"
                      fontSize="sm"
                      fontWeight="semibold"
                      style={{
                        marginBottom: "4px",
                        marginTop:
                          groups.today.length > 0 || groups.yesterday.length > 0
                            ? "8px"
                            : "0px",
                      }}
                    >
                      This Week
                    </Text>
                    {groups.thisWeek.map((thread) => {
                      const isCurrentThread = currentThreadId === thread._id;
                      const threadTitle =
                        thread.messages.find((msg: any) => msg.role === "user")
                          ?.content || `Conversation ${thread._id.slice(-6)}`;

                      return (
                        <div
                          key={thread._id}
                          onClick={() => {
                            onLoadThread?.(thread._id, thread);
                            setDropdownOpen(false);
                          }}
                          onMouseEnter={(e) => {
                            if (!isCurrentThread) {
                              e.currentTarget.style.backgroundColor =
                                "var(--color-bg-secondary-subtle)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isCurrentThread) {
                              e.currentTarget.style.backgroundColor =
                                "transparent";
                            }
                          }}
                          style={{
                            padding: "2px 4px",
                            marginBottom: "1px",
                            borderRadius: "2px",
                            cursor: "pointer",
                            transition: "background-color 0.2s",
                            backgroundColor: isCurrentThread
                              ? "var(--color-bg-brand-subtle)"
                              : "transparent",
                            border: isCurrentThread
                              ? "1px solid var(--color-border-brand)"
                              : "1px solid transparent",
                          }}
                        >
                          <Flex align="center" gap="xs">
                            <Text
                              fontSize="sm"
                              fontWeight="regular"
                              style={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: "180px",
                              }}
                            >
                              {threadTitle}
                            </Text>
                            {isCurrentThread && (
                              <Text
                                color="brand"
                                fontSize="xs"
                                fontWeight="medium"
                              >
                                (Current)
                              </Text>
                            )}
                          </Flex>
                        </div>
                      );
                    })}
                  </>
                )}

                {groups.thisMonth.length > 0 && (
                  <>
                    <Text
                      color="primary"
                      fontSize="sm"
                      fontWeight="semibold"
                      style={{
                        marginBottom: "4px",
                        marginTop:
                          groups.today.length > 0 ||
                          groups.yesterday.length > 0 ||
                          groups.thisWeek.length > 0
                            ? "8px"
                            : "0px",
                      }}
                    >
                      This Month
                    </Text>
                    {groups.thisMonth.map((thread) => {
                      const isCurrentThread = currentThreadId === thread._id;
                      const threadTitle =
                        thread.messages.find((msg: any) => msg.role === "user")
                          ?.content || `Conversation ${thread._id.slice(-6)}`;

                      return (
                        <div
                          key={thread._id}
                          onClick={() => {
                            onLoadThread?.(thread._id, thread);
                            setDropdownOpen(false);
                          }}
                          onMouseEnter={(e) => {
                            if (!isCurrentThread) {
                              e.currentTarget.style.backgroundColor =
                                "var(--color-bg-secondary-subtle)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isCurrentThread) {
                              e.currentTarget.style.backgroundColor =
                                "transparent";
                            }
                          }}
                          style={{
                            padding: "2px 4px",
                            marginBottom: "1px",
                            borderRadius: "2px",
                            cursor: "pointer",
                            transition: "background-color 0.2s",
                            backgroundColor: isCurrentThread
                              ? "var(--color-bg-brand-subtle)"
                              : "transparent",
                            border: isCurrentThread
                              ? "1px solid var(--color-border-brand)"
                              : "1px solid transparent",
                          }}
                        >
                          <Flex align="center" gap="xs">
                            <Text
                              fontSize="sm"
                              fontWeight="regular"
                              style={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: "180px",
                              }}
                            >
                              {threadTitle}
                            </Text>
                            {isCurrentThread && (
                              <Text
                                color="brand"
                                fontSize="xs"
                                fontWeight="medium"
                              >
                                (Current)
                              </Text>
                            )}
                          </Flex>
                        </div>
                      );
                    })}
                  </>
                )}
              </>
            );
          })()}
        </Dropdown>
      )}
    </div>
  );
});

export { AiSlideOutChatGlobal as AiSlideOutChatAdvanced };
